import type { FC } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { Size } from "../helpers/helpers.js";

const LOGO_SRC = "https://assets.alpharun.com/alpharun_white.png";

export interface PrivacyCurtainProps {
  /** Deterministic alias for the hidden workspace. */
  workspaceAlias?: string;
  /** Overrides the workspace heading, e.g. "HQ Hidden". */
  heading?: string;
  /** Overrides the reveal button label, e.g. "Show HQ". */
  buttonLabel?: string;
  /** Class overrides for the curtain surface, e.g. a different background. */
  className?: string;
  onReveal: () => void;
}

/**
 * Fully opaque full-viewport cover shown after the tab has been backgrounded
 * for a while, so switching to an Alpharun tab while screen sharing never
 * exposes the underlying page. Rendered through a portal on `document.body`
 * with a z-index above every overlay in the app, and painted as a solid
 * background — deliberately not a blur of the page underneath.
 */
export const PrivacyCurtain: FC<PrivacyCurtainProps> = (props) => {
  const { workspaceAlias, heading, buttonLabel, className, onReveal } = props;

  return createPortal(
    <div
      className={twMerge(
        "fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-white",
        className,
      )}
    >
      <img src={LOGO_SRC} alt="Alpharun" className="mb-6 h-8" />
      <div aria-hidden className="text-5xl">
        🔒
      </div>
      <h1 className="text-2xl font-semibold">
        {heading ?? workspaceAlias ?? "Workspace Hidden"}
      </h1>
      <Button
        variant={ButtonVariant.Secondary}
        size={Size.LARGE}
        className="mt-3"
        onClick={onReveal}
      >
        {buttonLabel ?? "Show Workspace"}
      </Button>
    </div>,
    document.body,
  );
};
